import { Injectable, NgZone } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';

import { ChartModel } from '../../../Common/Charts/models/chart.model';
import { Am5Chart } from '../../../Common/Charts/models/am5-chart.model';
import { printHelper } from '../../../Common/Misc/helpers/print/print.helper';
import { download } from '../../../Common/Misc/helpers/download/download.helper';
import { snapdom, preCache, BlobType, SnapdomOptions, LocalFont } from '@zumer/snapdom';
import { environment } from '@env/environment';

export interface SocialChartExportModel {
    title: string;
    exportData?: any[];
    exportLabelDefinitions?: Map<string, string>;
    // chart model if export data via amcharts
    chartModel?: ChartModel | Am5Chart;
}

export interface SnapDomStyleOverride {
    selector: string;
    style: Record<string, string>;
}

@Injectable()
export class SocialChartExportService {
    proxyUrl: string = environment.apiGatewayUrl + '/proxy';
    readonly snapDomProxyUrl: string = this.proxyUrl + '?responseType=blob';
    readonly snapDomLocalFonts: LocalFont[] = [
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-italic-400.woff2', weight: 400, style: 'italic' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-italic-500.woff2', weight: 500, style: 'italic' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-italic-600.woff2', weight: 600, style: 'italic' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-italic-700.woff2', weight: 700, style: 'italic' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-normal-400.woff2', weight: 400, style: 'normal' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-normal-500.woff2', weight: 500, style: 'normal' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-normal-600.woff2', weight: 600, style: 'normal' },
        { family: 'Poppins', src: '/assets/google/fonts/poppins/poppins-normal-700.woff2', weight: 700, style: 'normal' },
    ];
    // amCharts DOM that never paints (a11y elements, hidden HTML tooltips); skipping it avoids multi-second captures
    readonly snapDomExclude: string[] = [
        '.am5-focus-container',
        '.am5-tooltip-container',
        '.am5-reader-container',
        '.am5-html-container > [style*="display: none"]'
    ];

    constructor(public zone: NgZone) {}
    async exportImage(exportType: string, chartElementRef: any, title: string) {
        let url: string;
        switch (exportType) {
            case 'jpg':
                url = chartElementRef ? await htmlToImage.toJpeg(chartElementRef) : null;
                break;

            case 'png':
                url = chartElementRef ? await htmlToImage.toPng(chartElementRef) : null;
                break;

            case 'svg':
                url = chartElementRef ? await htmlToImage.toSvg(chartElementRef) : null;
                break;
            case 'print':
                const printData = chartElementRef ? await htmlToImage.toSvg(chartElementRef) : null;
                if (printData) {
                    printHelper.image(printData, document.title, {fit: chartElementRef.offsetHeight <= 1500});
                }
                break;
        }
        if (url) {
            download.url(url, `${title}.${exportType}`);
        }
    }
    async exportImageSnapDom(exportType: BlobType | string, chartElementRef: any, title: string) {
        const options: SnapdomOptions = {
            fast: true,
            embedFonts: true,
            width: chartElementRef.scrollWidth,
            height: chartElementRef.scrollHeight,
            localFonts: this.snapDomLocalFonts,
            scale: 1,
            dpr: window.devicePixelRatio,
            quality: 1,
            cache: 'full', // disabled | soft | auto | full
            useProxy: this.snapDomProxyUrl,
            exclude: this.snapDomExclude,
            excludeMode: 'remove'
        };

        if (exportType === 'print') {
            return this.zone.runOutsideAngular(async () => {
                const imgData = await snapdom.toSvg(chartElementRef,{...options, type: 'svg', format: 'svg'});

                if (imgData) {
                    printHelper.image(imgData, document.title, {fit: chartElementRef.offsetHeight <= 1500});
                }
            });
        } else {
            return this.zone.runOutsideAngular(async () => {
                const imgData = await snapdom.toBlob(chartElementRef,
                    {...options, type: exportType as BlobType, format: exportType as BlobType});

                if (imgData) {
                    download.blob(imgData, `${title}.${exportType}`);
                }
            });
        }
    }

    /**
     * Same as exportImageSnapDom, but first applies the style overrides to matching elements inside chartElementRef
     * (e.g. hiding export menus), and restores their original inline styles afterwards, even if the export fails.
     */
    async exportImageSnapDomWithOverrides(
        exportType: BlobType | string,
        chartElementRef: HTMLElement,
        title: string,
        overrides: SnapDomStyleOverride[]
    ) {
        const restores: (() => void)[] = [];
        overrides.forEach(({ selector, style }) => {
            chartElementRef.querySelectorAll<HTMLElement>(selector).forEach(element => {
                Object.entries(style).forEach(([property, value]) => {
                    const originalValue = element.style.getPropertyValue(property);
                    const originalPriority = element.style.getPropertyPriority(property);
                    element.style.setProperty(property, value);
                    restores.push(() => originalValue
                        ? element.style.setProperty(property, originalValue, originalPriority)
                        : element.style.removeProperty(property));
                });
            });
        });

        try {
            await this.exportImageSnapDom(exportType, chartElementRef, title);
        } finally {
            restores.forEach(restore => restore());
        }
    }

    /**
     * Preloads external resources (images, fonts) under the element so the first snapdom export doesn't stall.
     * Uses the same proxy/fonts as exportImageSnapDom so the cached entries are reused on export.
     */
    preCacheSnapDom(element: Element): Promise<void> {
        if (!element) {
            return Promise.resolve();
        }
        return this.zone.runOutsideAngular(() => preCache(element, {
            embedFonts: true,
            localFonts: this.snapDomLocalFonts,
            useProxy: this.snapDomProxyUrl,
            cache: 'full'
        }));
    }

    setExportDataForChartGroup(chartModel: ChartModel[]): any[] {
        const chartData  = [];
        let groupedData = [];
        const exportData = [];
        chartModel.forEach(chart => {
            chart.amchart.data.forEach(data => {
                if (data.label !== 'noData') {
                    chartData.push(data);
                }
            });
        });
        // Group chart data by label
        groupedData = chartData.reduce((acc, currentValue) => {
            const groupKey = currentValue['label'];
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(currentValue);
            return acc;
        }, []);

        //  Merge objects with the same key to one, so that all xValue can display in one row in csv
        for (const key in groupedData) {
            if (groupedData.hasOwnProperty(key)) {
                exportData.push(Object.assign({}, ...groupedData[key]));
            }
        }
        return exportData;
    }

    exportData(exportType: string, exportModel: SocialChartExportModel) {
        // export data via amcharts
        if (exportModel.chartModel) {
            exportModel.chartModel.export((exportType as keyof am4core.IExportOptions), exportModel.title);
            return;
        }

        let blob: Blob;
        const exportData = exportModel.exportData;
        //  Export CSV
        if (exportType === 'csv') {
            const csvLabels = [];
            const csvValues = [];
            const csvData = [
                csvLabels
            ];
            ;
            // Set label row
            for (const [key, value] of exportModel.exportLabelDefinitions?.entries()) {
                csvLabels.push(value);
            }
            // Set value row
            exportData.forEach((data) => {
                const chartValues = [];
                for (const [key, value] of exportModel.exportLabelDefinitions?.entries()) {
                    chartValues.push(data[key]);
                }
                csvValues.push(chartValues);
            });
            csvData.push(...csvValues);

            const csvString: string = papaparse.unparse(csvData, {delimiter: ','});
            blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
        } else if (exportType === 'json') {
            // Exporting JSON
            const jsonData = [];

            exportData.forEach((data) => {
                const obj = {};
                for (const [key, value] of exportModel.exportLabelDefinitions?.entries()) {
                    obj[value] = data[key];
                }
                jsonData.push(obj);
            });
            blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
        }

        if (blob) {
            download.blob(blob, `${exportModel.title}.${exportType}`);

        }
    }
}
